import {
    type FC,
    useEffect,
    useRef,
    useState,
} from "react";
import FontAwesomeLight from "../../../../../app/Utilities/Platform/FontAwesome/FontAwesomeLight";
import { type NavigationType } from "../Navigation";
import { NavigationSearchResults } from "./NavigationSearchResults";

interface Props {
    open: boolean;
    setOpen: (open: boolean) => void;
    navigation: NavigationType;
}

export const NavigationSearchPopup: FC<Props> = ({ open, setOpen, navigation }) => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const [ search, setSearch ] = useState("");

    useEffect(() => {
        if(!open) {
            return;
        }

        // Focus on search on open when
        setSearch("");
        inputRef.current?.focus();

        const clickListener = (e: MouseEvent) => {
            if(!wrapperRef.current) {
                setOpen(false);

                return;
            }

            if(!(e.target instanceof Element)) {
                setOpen(false);

                return;
            }

            // Check to see if the mouse event is inside the navigation search window
            if(wrapperRef.current.contains(e.target)) {
                return;
            }

            setOpen(false);
        };

        const keyListener = (e: KeyboardEvent) => {
            if(e.key === "Escape") {
                setOpen(false);
            }
        };

        window.addEventListener("mousedown", clickListener);
        window.addEventListener("keyup", keyListener);

        return () => {
            window.removeEventListener("mousedown", clickListener);
            window.removeEventListener("keyup", keyListener);
        };
    }, [ open, setOpen ]);

    return (
        <div
            inert={!open}
            className={
                "absolute inset-0 bg-black/10 backdrop-blur-xs py-32 px-16 z-10 " +
                "transition-opacity " + (
                    open ?
                        "opacity-100" :
                        "opacity-0 pointer-events-none"
                )
            }
        >
            <div
                ref={wrapperRef}
                className={
                    "w-full bg-white rounded-[25.5px] shadow max-w-[theme(screens.md)] mx-auto max-h-[65vh] flex " +
                    "flex-col overflow-hidden"
                }
                onBlur={e => {
                    if(!e.relatedTarget) {
                        return;
                    }

                    if(e.relatedTarget !== e.currentTarget && !e.currentTarget.contains(e.relatedTarget)) {
                        setOpen(false);
                    }
                }}
            >
                <div className="w-full px-4 pt-4 mb-2 box-border">
                    <div className="form-input">
                        <div className="input-icon left">
                            <FontAwesomeLight icon="search" />
                        </div>
                        <input
                            placeholder="Search..."
                            ref={inputRef}
                            value={search}
                            onChange={e => {
                                setSearch(e.currentTarget.value);
                            }}
                        />
                    </div>
                </div>
                <div className="border-0 border-t border-t-neutral-200 border-solid flex-1 overflow-auto">
                    <NavigationSearchResults
                        search={search}
                        navigation={navigation}
                        open={open}
                        setOpen={setOpen}
                    />
                </div>
            </div>
        </div>
    );
};
